import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchApi } from '../api';
import { clearLinkToken, queueNotice, readLinkToken } from '../utils/authLinks';
import { CheckCircle2, Clock3, KeyRound, LogIn, Mail, ShieldCheck, UserPlus, Users } from 'lucide-react';
import '../styles/account.css';

type Mode = 'login' | 'register' | 'forgot' | 'reset';

interface InvitePreview {
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  email: string;
  role: string;
  roleName: string;
  organizationName: string;
  inviterName: string | null;
  expiresAt: string;
  accountExists: boolean;
  accountName: string | null;
}

const CONSENT_LINK = '#legal';

export const AuthModal: React.FC = () => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Tokens that arrived from a letter.
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [inviteProblem, setInviteProblem] = useState<string | null>(null);

  const isRegisterMode = mode === 'register';

  useEffect(() => {
    const reset = readLinkToken('reset');
    if (reset) {
      setResetToken(reset);
      setMode('reset');
    }

    // A confirmation link opened in a browser without a session: consume it here
    // so the visitor is not told to "log in first" to click a link they already
    // clicked.
    const verify = readLinkToken('verify');
    if (verify) {
      clearLinkToken('verify');
      fetchApi('/auth/verify-email', { method: 'POST', body: JSON.stringify({ token: verify }) })
        .then(() => setNotice('Email подтверждён. Войдите в аккаунт — подключение каналов и оплата тарифа уже доступны.'))
        .catch((err: any) => setError(err.message || 'Не удалось подтвердить email. Запросите новое письмо после входа.'));
    }

    const inviteLink = readLinkToken('invite');
    if (!inviteLink) return;
    setInviteToken(inviteLink);
    fetchApi(`/invites/${inviteLink}`)
      .then((preview: InvitePreview) => {
        setInvite(preview);
        setEmail(preview.email);
        if (preview.status !== 'pending') {
          setInviteProblem(preview.status === 'expired'
            ? 'Срок действия приглашения истёк. Попросите владельца пространства отправить новое письмо.'
            : preview.status === 'accepted'
              ? 'Это приглашение уже использовано. Войдите в аккаунт обычным способом.'
              : 'Приглашение отозвано владельцем пространства.');
          setInviteToken(null);
          return;
        }
        setMode(preview.accountExists ? 'login' : 'register');
      })
      .catch((err: any) => {
        setInviteProblem(err.message || 'Приглашение не найдено. Проверьте ссылку из письма.');
        setInviteToken(null);
      });
  }, []);

  const switchMode = useCallback((next: Mode) => {
    setMode(next);
    setError(null);
    setNotice(null);
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setLoading(true);

    try {
      if (mode === 'forgot') {
        const result = await fetchApi('/auth/forgot-password', {
          method: 'POST',
          body: JSON.stringify({ email: email.trim() }),
        });
        setNotice(result?.message
          || 'Если аккаунт с таким адресом существует, мы отправили письмо со ссылкой для смены пароля.');
        return;
      }

      if (mode === 'reset') {
        const result = await fetchApi('/auth/reset-password', {
          method: 'POST',
          body: JSON.stringify({ token: resetToken, password }),
        });
        clearLinkToken('reset');
        setResetToken(null);
        setPassword('');
        setMode('login');
        setNotice(result?.message || 'Пароль изменён. Войдите с новым паролем.');
        return;
      }

      if (mode === 'register') {
        const result = await register({
          name,
          email,
          password,
          company,
          consent,
          inviteToken: inviteToken || undefined,
        });
        clearLinkToken('invite');
        // The screen unmounts the moment the session lands, so anything the user
        // still needs to know is handed to the application shell.
        if (result?.invite?.error) {
          queueNotice({ tone: 'warn', text: `Аккаунт создан, но присоединиться к команде не удалось: ${result.invite.error}` });
        } else if (result?.invite?.accepted) {
          queueNotice({ tone: 'info', text: `Вы присоединились к «${result.invite.organizationName}».` });
        }
        return;
      }

      const result = await login(email, password, { inviteToken: inviteToken || undefined });
      clearLinkToken('invite');
      if (result?.invite?.error) {
        queueNotice({ tone: 'warn', text: `Вы вошли в свой аккаунт, но присоединиться к команде не удалось: ${result.invite.error}` });
      } else if (result?.invite?.accepted) {
        queueNotice({ tone: 'info', text: `Вы присоединились к «${result.invite.organizationName}».` });
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка аутентификации');
    } finally {
      setLoading(false);
    }
  };

  /**
   * The demo shortcut used to fill in `demo@omnisale.ru` / `demo1234`, which meant
   * a working credential pair for a seeded account shipped inside the browser
   * bundle of every deployment — readable by anyone who opened the file.
   *
   * The seed no longer creates a fixed password either: it generates one and prints
   * it to the console once. So there is nothing to prefill, and the button is gone
   * rather than replaced by a shortcut that quietly stops working.
   */

  const heading = {
    login: invite ? `Присоединиться к «${invite.organizationName}»` : 'Вход в Clientix',
    register: invite ? `Регистрация в «${invite.organizationName}»` : 'Регистрация в Clientix',
    forgot: 'Восстановление пароля',
    reset: 'Новый пароль',
  }[mode];

  const subheading = {
    login: invite ? 'Войдите, чтобы принять приглашение' : 'Продолжите работу с обращениями и сделками',
    register: invite ? 'Создайте аккаунт — доступ к пространству добавится сразу' : 'Создайте новый аккаунт компании',
    forgot: 'Укажите адрес, на который зарегистрирован аккаунт — пришлём ссылку для смены пароля',
    reset: 'Придумайте пароль не короче 12 символов. Все остальные сессии будут завершены',
  }[mode];

  const submitLabel = {
    login: <><LogIn size={17} /> Войти</>,
    register: <><UserPlus size={17} /> Зарегистрироваться</>,
    forgot: <><Mail size={17} /> Отправить ссылку</>,
    reset: <><KeyRound size={17} /> Сохранить пароль</>,
  }[mode];

  return (
    <div className="auth-screen" role="dialog" aria-modal="true" aria-labelledby="auth-title">
      <div className="auth-shell">
        <aside className="auth-intro">
          <div className="auth-brand">
            <span className="auth-brand-icon"><ShieldCheck size={24} /></span>
            <span>Clientix</span>
          </div>
          <div>
            <p className="auth-eyebrow">Единый центр продаж</p>
            <h1>Клиенты, сделки и AI-менеджер — в одном рабочем окне.</h1>
            <p>Подключайте каналы, отвечайте быстрее и не теряйте обращения между мессенджерами.</p>
          </div>
          <ul className="auth-benefits">
            <li>Все диалоги и заявки в одном месте</li>
            <li>AI-ответы и контроль воронки 24/7</li>
            <li>Демо-доступ без настройки интеграций</li>
          </ul>
        </aside>

        <main className="auth-panel">
          <div className="auth-head">
            <h2 id="auth-title">{heading}</h2>
            <p>{subheading}</p>
          </div>

          {invite && invite.status === 'pending' && (
            <div className="auth-invite-card">
              <span>Приглашение в команду</span>
              <strong>
                {invite.inviterName ? `${invite.inviterName} приглашает вас` : 'Вас приглашают'} в «{invite.organizationName}»
              </strong>
              <p>
                Роль: {invite.roleName}. Приглашение выписано на {invite.email} —
                {invite.accountExists
                  ? ' войдите под этим адресом, и доступ добавится сразу.'
                  : ' зарегистрируйтесь с этим адресом, и вы попадёте прямо в команду.'}
              </p>
            </div>
          )}

          {inviteProblem && (
            <div className="auth-notice warn" role="status">
              <Clock3 size={16} aria-hidden="true" />
              <span>{inviteProblem}</span>
            </div>
          )}

          {notice && (
            <div className="auth-notice" role="status">
              <CheckCircle2 size={16} aria-hidden="true" />
              <span>{notice}</span>
            </div>
          )}

          {error && <div className="auth-error" role="alert">{error}</div>}

          <form onSubmit={handleSubmit} className="auth-form">
            {isRegisterMode && (
              <div className="auth-field">
                <label htmlFor="auth-name">Ваше имя</label>
                <input id="auth-name" type="text" value={name} onChange={e => setName(e.target.value)} required placeholder="Иван Иванов" autoComplete="name" />
              </div>
            )}

            {mode !== 'reset' && (
              <div className="auth-field">
                <label htmlFor="auth-email">Email</label>
                <input
                  id="auth-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  readOnly={Boolean(invite && invite.status === 'pending')}
                  placeholder="user@omnisale.ru"
                  autoComplete="email"
                />
                {invite && invite.status === 'pending' && (
                  <p className="auth-hint">Адрес задан приглашением и не может быть изменён.</p>
                )}
              </div>
            )}

            {mode !== 'forgot' && (
              <div className="auth-field">
                <label htmlFor="auth-password">{mode === 'reset' ? 'Новый пароль' : 'Пароль'}</label>
                <input
                  id="auth-password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  minLength={mode === 'login' ? undefined : 12}
                  placeholder="••••••••"
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                />
                {mode !== 'login' && <p className="auth-hint">Не менее 12 символов.</p>}
              </div>
            )}

            {isRegisterMode && (
              <div className="auth-field">
                <label htmlFor="auth-company">Название компании</label>
                <input id="auth-company" type="text" value={company} onChange={e => setCompany(e.target.value)} placeholder="ООО «ПромТорг»" autoComplete="organization" />
              </div>
            )}

            {isRegisterMode && (
              <label className="auth-consent">
                <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} required />
                <span>
                  Я согласен с <a href={CONSENT_LINK}>условиями использования и политикой обработки персональных данных</a>
                  {' '}и даю согласие на обработку моих персональных данных.
                </span>
              </label>
            )}

            <button type="submit" disabled={loading || (isRegisterMode && !consent)} className="auth-submit">
              {loading ? 'Загрузка...' : submitLabel}
            </button>
          </form>

          <div className="auth-actions">
            {mode === 'login' && (
              <div className="auth-inline-actions">
                <button type="button" className="auth-link-btn" onClick={() => switchMode('forgot')}>
                  Забыли пароль?
                </button>
                {!invite && (
                  <button type="button" className="auth-link-btn muted" onClick={() => switchMode('register')}>
                    Нет аккаунта? Зарегистрироваться
                  </button>
                )}
              </div>
            )}

            {mode === 'register' && !invite && (
              <button type="button" onClick={() => switchMode('login')} className="auth-switch">
                Уже есть аккаунт? Войти
              </button>
            )}

            {mode === 'register' && invite && (
              <button type="button" onClick={() => switchMode('login')} className="auth-switch">
                <Users size={15} /> У меня уже есть аккаунт Clientix — войти
              </button>
            )}

            {(mode === 'forgot' || mode === 'reset') && (
              <button type="button" onClick={() => { switchMode('login'); clearLinkToken('reset'); setResetToken(null); }} className="auth-switch">
                Вернуться к входу
              </button>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};
